import * as B from '@blueprintjs/core';
import React from 'react';

import {recordEvent} from 'app/tools/Analytics';
import * as imageryUtils from 'app/utils/imageryUtils';

import {MAX_FRAMES} from './frames';
import PlaybackControls from './PlaybackControls';
import ProgressLine from './ProgressLine';
import cs from './styles.module.scss';
import {ImageryTimelineState} from './useImageryTimeline';

interface Props {
  timeline: ImageryTimelineState;
  onClose: () => void;
}

const ImageryTimeline: React.FunctionComponent<Props> = ({timeline, onClose}) => {
  const {
    layerKey,
    frames,
    images,
    hasTooManyFrames,
    frameIndex,
    setFrameIndex,
    isPlaying,
    setIsPlaying,
    isBuffering,
  } = timeline;
  const loadedCount = frames.filter((f) => images[f.cursor] !== undefined).length;
  const frame = frames[frameIndex];
  const canPlay = frames.length > 1;

  const togglePlaying = React.useCallback(() => {
    if (!isPlaying) {
      recordEvent('Played imagery timeline');
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, setIsPlaying]);

  const step = React.useCallback(
    (offset: number) => {
      setIsPlaying(false);
      setFrameIndex(Math.min(Math.max(frameIndex + offset, 0), frames.length - 1));
    },
    [frameIndex, frames.length, setFrameIndex, setIsPlaying]
  );

  const hotkeys = React.useMemo(
    () => [
      {
        global: true,
        combo: 'space',
        label: 'Play or pause the imagery timeline',
        disabled: !canPlay,
        preventDefault: true,
        onKeyDown: togglePlaying,
      },
      {
        global: true,
        combo: 'left',
        label: 'Previous imagery timeline scene',
        onKeyDown: () => step(-1),
      },
      {
        global: true,
        combo: 'right',
        label: 'Next imagery timeline scene',
        onKeyDown: () => step(1),
      },
    ],
    [canPlay, togglePlaying, step]
  );
  B.useHotkeys(hotkeys);

  let status: string | null = null;
  if (hasTooManyFrames) {
    status = `More than ${MAX_FRAMES} scenes in the last year.`;
  } else if (!frames.length) {
    status = 'No scenes in the last year.';
  } else if (loadedCount < frames.length) {
    status = `Loading ${loadedCount} of ${frames.length}…`;
  } else if (isBuffering) {
    status = 'Buffering…';
  } else if (frame && images[frame.cursor] === null) {
    status = 'Scene unavailable';
  }

  return (
    <div className={cs.bar}>
      <div className={cs.row}>
        <PlaybackControls
          isPlaying={isPlaying}
          canPlay={canPlay}
          onTogglePlaying={togglePlaying}
          canStepBack={!!frame && frameIndex > 0}
          canStepForward={!!frame && frameIndex < frames.length - 1}
          onStep={step}
          dateLabel={frame ? imageryUtils.formatDate(frame.cursor, layerKey) : '–'}
          positionLabel={frame ? `Scene ${frameIndex + 1} of ${frames.length}` : ''}
        />
        <div className={cs.spacer} />
        {status && (
          <div className={cs.status} title={status}>
            {status}
          </div>
        )}
        <B.Tooltip content="Close imagery timeline">
          <B.Button minimal icon="cross" aria-label="Close imagery timeline" onClick={onClose} />
        </B.Tooltip>
      </div>
      {frame && (
        <ProgressLine
          fraction={frames.length > 1 ? frameIndex / (frames.length - 1) : 1}
          loaded={frames.map((f) => images[f.cursor] !== undefined)}
          label={`Scene ${frameIndex + 1} of ${frames.length}`}
          onSeek={(fraction) => {
            setIsPlaying(false);
            setFrameIndex(Math.round(fraction * (frames.length - 1)));
          }}
        />
      )}
    </div>
  );
};

export default ImageryTimeline;
